"use client";
import { useTranslations } from "next-intl";
import React, {
  useEffect,
  useState,
  useTransition,
  useActionState,
  use,
} from "react";
import GooglePlayButton from "@/mocks/assets/footerAssets/googleplaybadge.png";
import AppStoreButton from "@/mocks/assets/footerAssets/appstorebadge.svg";
import Image from "next/image";
import CloverIcon from "../_icons/cloverIcon";
import LanguageIcon from "../languageIcon/languageIcon";
import { localeOptions } from "../_localized/localizationData";
import { usePathname } from "next/navigation";
import {
  MerchantFooter,
  createAddressString,
} from "../merchantFooter/merchantFooter";
import { Service } from "@/dto/service";
import { FooterSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { getMerchantGeoAction } from "@/app/actions";
import { Merchant } from "@/dto/merchant";
import { useCookies } from "react-cookie";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";

export interface FooterProps {
  termsOfServiceUrl: string;
  privacyPolicyUrl: string;
  googlePlayUrl: string;
  appStoreUrl: string;
  learnMoreUrl: string;
  languageString: string;
  policiesPromise?: Promise<any>;
  merchant: Merchant;
}

export const Footer = (props: FooterProps) => {
  const t = useTranslations("Footer");
  const pathname = usePathname();
  const [classNames, setClassNames] = useState([
    "w-full",
    "bg-[#F8F8F8]",
    "text-[#000000]",
    "flex",
    "flex-col",
    "gap-[36px]",
    "px-4",
    "sm:px-10",
    "pb-2",
    "text-[15px]",
  ]);
  const { policiesPromise, merchant } = props;

  const isCheckoutPage: boolean =
    pathname?.includes("/checkout") ||
    pathname?.includes("/guest-checkout") ||
    false;
  const isProfilePage: boolean =
    pathname?.includes("/customer-profile") ||
    pathname?.includes("/verify-email") ||
    false;
  const isConfirmationPage: boolean =
    pathname?.includes("/confirmation") || false;
  const isOrderHistoryPage: boolean = pathname?.includes("/my-orders") || false;
  const [cookies] = useCookies(["scanToOrderInfo"]);
  const { scanToOrderInfo } = cookies;
  const isScanToOrderFlow = !!scanToOrderInfo;
  let isRunningTabsEnabled = false;

  useEffect(() => {
    if (pathname === "/guest-checkout" || pathname === "/checkout") {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter(
          (className) => className !== "pb-2"
        );
        return [...tempClasses, "pb-16"];
      });
    } else if (pathname === "/confirmation") {
      if (isScanToOrderFlow && !isRunningTabsEnabled) {
        setClassNames((prevClasses) => {
          const tempClasses = prevClasses.filter(
            (className) => className !== "pb-2"
          );
          if (tempClasses.includes("mb-16")) {
            return tempClasses;
          }
          return [...tempClasses, "mb-16"];
        });
      }
    } else {
      setClassNames((prevClasses) => {
        const tempClasses = prevClasses.filter((name) => name !== "pb-16");
        return [...tempClasses, "pb-2"];
      });
    }
  }, [pathname]);

  const [isTransitioning, startTransition] = useTransition();

  const [merchantGeoState, getMerchantGeoClientAction, isMerchantGeoLoading] =
    useActionState(getMerchantGeoAction, { data: null, error: null });

  useEffect(() => {
    if (merchant) {
      startTransition(() => {
        getMerchantGeoClientAction({
          address: createAddressString(merchant.address),
        });
      });
    }
  }, [merchant.id]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    if (!isTransitioning && !isMerchantGeoLoading) {
      setIsLoading(false);
    }
  }, [isTransitioning, isMerchantGeoLoading]);

  const policies = policiesPromise && use(policiesPromise);

  const isSimpleStorefront = merchant?.merchantMeta.selectedSetup === "Simple";
  const isPreviewSite =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("preview-");
  const isStandaloneGiftCard =
    merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;
  const service = merchant?.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const currentLocale = merchant?.locale as keyof typeof localeOptions;
  const languageString =
    localeOptions[currentLocale]?.localizationString ?? props.languageString;

  const shouldShowMerchantFooter =
    !isSimpleStorefront &&
    !isCheckoutPage &&
    !isProfilePage &&
    !isConfirmationPage &&
    !isOrderHistoryPage &&
    !isStandaloneGiftCard;

  isRunningTabsEnabled = merchant?.isRunningTabsEnabled
    ? merchant?.isRunningTabsEnabled
    : false;

  return (
    <div
      data-testid={applyDataTestId(pom.footer.footer)}
      className="w-full bg-[#F8F8F8]"
    >
      {(isLoading || !merchant) && shouldShowMerchantFooter && (
        <FooterSkeleton count={5} />
      )}
      {!isLoading && merchant && shouldShowMerchantFooter && (
        <div className="flex-1 flex flex-col w-full mx-auto">
          <MerchantFooter
            merchant={merchant}
            merchantName={merchant.name}
            merchantAddress={merchant.address}
            merchantPhoneNumber={merchant.phone}
            merchantEmail={merchant.customerContactEmail}
            merchantGeo={merchantGeoState.data}
            isBusinessHourAvailable={merchant.isBusinessHourAvailable}
            businessHours={
              merchant.isRestaurant
                ? service?.hours
                : merchant?.businessHours?.hours
            }
            facebookUrl={merchant.merchantMeta.socialMediaFacebookUrl}
            instagramUrl={merchant.merchantMeta.socialMediaInstagramUrl}
            timezone={merchant.timezone}
            showLinks={policies?.showLinks}
            locale={merchant.locale}
          />
        </div>
      )}
      <div
        data-testid={applyDataTestId(pom.footer.cloverFooter)}
        className={classNames.join(" ")}
      >
        <div className="w-full border-t-[1px] border-[#727272]"></div>
        <div className="flex flex-col sm:flex-row justify-between">
          <div className=" flex flex-col items-start content-center mb-6 sm:w-3/5">
            <CloverIcon />
            <div className="flex flex-row items-start mt-8 sm:mt-6">
              <div className="text-left sm:text-center text-[15px]">
                <span>
                  <a
                    href={`https://www.clover.com/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[15px]"
                  >
                    {t("poweredBy", { year: new Date().getFullYear() })}
                  </a>
                </span>
                &nbsp;&#8226;&nbsp;
                <span>
                  <a
                    href={`https://www.clover.com/consumer/terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[15px]"
                  >
                    {t("cloverTerms")}
                  </a>
                </span>
                &nbsp;&#8226;&nbsp;
                <span>
                  <a
                    href={`https://www.clover.com/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[15px]"
                  >
                    {t("cloverPrivacyPolicy")}
                  </a>
                </span>
              </div>
            </div>
            <p
              data-testid={applyDataTestId(pom.footer.description)}
              className="text-left font-normal text-[15px]"
            >
              {t.rich("trademarks", {
                googlePrivacyPolicyLink: (chunks) => (
                  <a
                    href={`https://policies.google.com/privacy?hl=en`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[15px]"
                  >
                    {chunks}
                  </a>
                ),
                termsOfServiceLink: (chunks) => (
                  <a
                    href={`https://policies.google.com/terms?hl=en`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[15px]"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>
          </div>

          <div className="flex flex-col justify-start sm:justify-between items:start sm:items-end mb-6 gap-5 sm:gap-0">
            {merchant?.isRestaurant && merchant?.locale === "en-US" && (
              <div className="flex flex-col w-full justify-center items-start sm:items-end">
                <p className="text-center sm:text-left text-[15px]">
                  {t("orderAhead")}
                </p>
                <div className="flex flex-row w-full justify-between sm:justify-start">
                  <a
                    href={props.googlePlayUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Image
                      className="mt-3 h-14"
                      src={GooglePlayButton.src}
                      height={10}
                      width={180}
                      alt={t("googlePlayButtonAlt")}
                    />
                  </a>
                  <a href={props.appStoreUrl} target="_blank" rel="noreferrer">
                    <Image
                      className="mt-3 h-14"
                      src={AppStoreButton.src}
                      height={50}
                      width={180}
                      alt={t("appStoreButtonAlt")}
                    />
                  </a>
                </div>
              </div>
            )}
            <div className="text-[14px]/5 flex flex-row space-x-3 items-center">
              <LanguageIcon
                locale={merchant?.locale.toLocaleLowerCase() || "en-us"}
                width="20"
                height="20"
                role="img"
                aria-label={t("languageIconAlt")}
              />
              <p className="text-[15px]">{languageString}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
